import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, TicketPercent, X } from "lucide-react";
import { toast } from "sonner";
import { api, adminCall, formatApiError } from "../../api";

const EMPTY = { code: "", kind: "percent", value: "", max_uses: "", expires_at: "", active: true };

const input =
  "w-full rounded-xl border border-sage/20 bg-white px-4 py-2.5 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-sage";

export default function PromosPanel() {
  const [promos, setPromos] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    adminCall(() => api.get("/admin/promos"))
      .then((r) => setPromos(r.data.promos))
      .catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    setBusy(true);
    try {
      await adminCall(() =>
        api.post("/admin/promos", {
          code: form.code.trim(),
          percent_off: form.kind === "percent" ? parseFloat(form.value) : null,
          amount_off: form.kind === "amount" ? parseFloat(form.value) : null,
          max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
          expires_at: form.expires_at ? new Date(form.expires_at + "T23:59:59Z").toISOString() : null,
          active: true,
        })
      );
      toast.success(`Code ${form.code.toUpperCase()} is live`);
      setForm(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Could not create the code."));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (p) => {
    await adminCall(() =>
      api.put(`/admin/promos/${p.id}`, {
        code: p.code,
        percent_off: p.percent_off,
        amount_off: p.amount_off_cents ? p.amount_off_cents / 100 : null,
        max_uses: p.max_uses,
        expires_at: p.expires_at,
        active: !p.active,
      })
    ).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete code ${p.code}? Customers who already used it keep their downloads.`)) return;
    await adminCall(() => api.delete(`/admin/promos/${p.id}`)).catch((e) => toast.error(formatApiError(e)));
    toast.success("Code deleted");
    load();
  };

  return (
    <div data-testid="admin-promos-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-2xl text-ink">Promo codes</h2>
          <p className="mt-1 text-sm text-ink/50">
            Buyers enter codes on the product page or directly at Stripe checkout.
          </p>
        </div>
        <button
          onClick={() => setForm({ ...EMPTY })}
          data-testid="admin-add-promo-btn"
          className="flex items-center gap-2 rounded-full bg-sage px-5 py-2.5 text-xs font-medium uppercase tracking-[0.12em] text-cream transition-all hover:-translate-y-0.5 hover:bg-sage-deep"
        >
          <Plus size={14} /> New code
        </button>
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-sage/12 bg-white/80" data-testid="admin-promos-table">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead>
            <tr className="border-b border-sage/10 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/45">
              <th className="px-5 py-4">Code</th>
              <th className="px-5 py-4">Discount</th>
              <th className="px-5 py-4">Used</th>
              <th className="px-5 py-4">Expires</th>
              <th className="px-5 py-4">Status</th>
              <th className="px-5 py-4"></th>
            </tr>
          </thead>
          <tbody>
            {(promos || []).map((p) => (
              <tr key={p.id} className="border-b border-sage/5 last:border-0" data-testid={`promo-row-${p.code}`}>
                <td className="px-5 py-4 font-mono font-medium tracking-wider text-ink">{p.code}</td>
                <td className="px-5 py-4 text-ink/70">
                  {p.percent_off ? `${p.percent_off}% off` : `$${(p.amount_off_cents / 100).toFixed(2)} off`}
                </td>
                <td className="px-5 py-4 text-ink/60">
                  {p.times_used}{p.max_uses ? ` / ${p.max_uses}` : ""}
                </td>
                <td className="px-5 py-4 text-ink/60">
                  {p.expires_at ? new Date(p.expires_at).toLocaleDateString() : "Never"}
                </td>
                <td className="px-5 py-4">
                  <button onClick={() => toggle(p)} data-testid={`promo-toggle-${p.code}`}
                    className={`rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
                      p.active ? "bg-sage-tint text-sage-deep hover:bg-sage hover:text-cream" : "bg-cream-deep text-ink/45 hover:bg-ink/10"
                    }`}>
                    {p.active ? "Active" : "Paused"}
                  </button>
                </td>
                <td className="px-5 py-4 text-right">
                  <button onClick={() => remove(p)} data-testid={`promo-delete-${p.code}`}
                    className="rounded-full border border-red-200 p-2 text-red-500 transition-colors hover:bg-red-500 hover:text-white" aria-label="Delete">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {promos && promos.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-ink/45">No codes yet — create one for your launch or ads campaign.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4 backdrop-blur-sm" data-testid="promo-editor-modal">
          <div className="w-full max-w-md rounded-[1.6rem] bg-cream p-8 shadow-2xl">
            <div className="flex items-center justify-between">
              <h3 className="flex items-center gap-2 font-serif text-2xl text-ink">
                <TicketPercent size={20} className="text-lavender-deep" /> New promo code
              </h3>
              <button onClick={() => setForm(null)} data-testid="promo-editor-close" className="rounded-full p-2 text-ink/50 hover:bg-cream-dark" aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div className="mt-6 space-y-4">
              <input className={`${input} font-mono uppercase tracking-wider`} data-testid="promo-code-field" placeholder="CODE (e.g. LAUNCH20)"
                value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
              <div className="grid grid-cols-2 gap-4">
                <select className={input} data-testid="promo-kind-select" value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                  <option value="percent">Percent off</option>
                  <option value="amount">Fixed $ off</option>
                </select>
                <input className={input} data-testid="promo-value-field" type="number" min="1" step="1"
                  placeholder={form.kind === "percent" ? "e.g. 20" : "e.g. 5"}
                  value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <input className={input} data-testid="promo-max-uses-field" type="number" min="1" placeholder="Max uses (optional)"
                  value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} />
                <input className={input} data-testid="promo-expiry-field" type="date"
                  value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
              </div>
              <button onClick={create} disabled={busy || !form.code.trim() || !form.value} data-testid="promo-create-btn"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-sage py-3.5 text-sm font-medium text-cream transition-colors hover:bg-sage-deep disabled:opacity-60">
                {busy && <Loader2 size={15} className="animate-spin" />}
                Create code
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
